import { isValidElement, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * The one way this app explains a badge, a control or a column: a shadcn
 * tooltip, never a native `title`. Native titles can't be styled, can't hold
 * structure, and open on an OS delay that reads as lag next to the Radix ones.
 *
 * A plain-text child is wrapped in a hoverable span so "there's an explanation
 * here" looks the same everywhere; an element child becomes the trigger itself
 * and keeps its own styling. Passing no content renders the child alone, so a
 * call site with an optional note doesn't have to branch.
 */
export function Hint({
  content,
  children,
  side = 'top',
  align,
  className,
  contentClassName,
  underline = true,
  delayDuration = 300,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Applied to the generated span — only when children is plain text. */
  className?: string;
  contentClassName?: string;
  /** The dotted rule marking hintable text. Off for bare numbers and icons. */
  underline?: boolean;
  delayDuration?: number;
}) {
  if (content == null || content === '' || content === false) {
    return <>{children}</>;
  }
  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>
        {isValidElement(children) ? children : (
          <span
            className={cn(
              'cursor-help',
              underline && 'underline decoration-dotted decoration-current/40 underline-offset-2',
              className
            )}
          >
            {children}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        className={cn('max-w-[20rem] text-left leading-relaxed', contentClassName)}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
